import { Link } from "react-router-dom";
import type { Article } from "@/mocks/news";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "wide" | "compact";
  rank?: number;
}

export default function ArticleCard({ article, variant = "default", rank }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-b-0 cursor-pointer"
      >
        {rank ? (
          <span
            className={`shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-bold font-heading ${
              rank <= 3
                ? "bg-primary-500 text-background-50"
                : "bg-background-200 text-foreground-600"
            }`}
          >
            {rank}
          </span>
        ) : null}
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500 font-label flex items-center gap-2">
            <span className="text-secondary-600">{article.categoryName}</span>
            <span>{article.time}</span>
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full h-40 sm:h-[136px] rounded-md overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 独家吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col min-w-0 py-0.5">
          <span className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-[11px] font-label mb-2">
            {article.categoryName}
          </span>
          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 font-label">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500 font-label">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.date}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-full h-48 overflow-hidden bg-background-200 relative">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 独家吃瓜`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-background-50/90 text-foreground-800 text-[11px] font-label">
          {article.categoryName}
        </span>
      </div>
      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 font-label flex-1">
          {article.excerpt}
        </p>
        <div className="mt-3 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500 font-label">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line"></i>
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}